import { CircleAlert, CircleCheck, Clock, FileText, KeyRound, Link2Off, LucideIcon, MailCheck } from 'lucide-react';
import { ReactNode } from 'react';
import { Button } from '../../../../ui/components/ui/button';
import { OfferCheckoutStatus } from '../../constants/offer-checkout-status.enum';
import { CONTACT_MAIL } from '../../constants/offer-checkout.constants';

/** Every state of the public page that shows instead of the checkout. */
export type OfferCheckoutNoticeStatus = Exclude<OfferCheckoutStatus, OfferCheckoutStatus.LOADING | OfferCheckoutStatus.READY>;

interface NoticeContent {
  icon: LucideIcon;
  title: string;
  body: ReactNode;
  action?: ReactNode;
}

const LINK_CLASS = 'font-bold underline underline-offset-2';
const ACTION_CLASS = 'h-11 w-full bg-[#5FD5DE] font-bold text-white hover:bg-[#5FD5DE]/90';

const contactLink = (
  <a href={`mailto:${CONTACT_MAIL}`} className={LINK_CLASS}>
    {CONTACT_MAIL}
  </a>
);

const MailItem = ({ icon: Icon, children }: { icon: LucideIcon; children: ReactNode }) => (
  <li className="flex items-center gap-3 rounded-xl bg-white px-4 py-3">
    <Icon className="h-4 w-4 shrink-0 text-[#082729]/50" />
    <span>{children}</span>
  </li>
);

const NOTICES: Record<OfferCheckoutNoticeStatus, NoticeContent> = {
  [OfferCheckoutStatus.LINK_INVALID]: {
    icon: Link2Off,
    title: 'Dieser Link ist ungültig oder abgelaufen',
    body: <p>Bitte melde dich bei {contactLink} - wir helfen dir gerne weiter!</p>
  },
  [OfferCheckoutStatus.ALREADY_ACCEPTED]: {
    icon: CircleCheck,
    title: 'Dieses Angebot wurde bereits angenommen',
    body: <p>Du kannst dich mit deiner E-Mail-Adresse einloggen.</p>,
    action: (
      <Button asChild className={ACTION_CLASS}>
        <a href="/">Zum Login</a>
      </Button>
    )
  },
  [OfferCheckoutStatus.TOO_MANY_REQUESTS]: {
    icon: Clock,
    title: 'Zu viele Anfragen',
    body: (
      <>
        <p>Bitte versuche es in ein paar Minuten erneut.</p>
        <p>Sollte dieser Fehler weiterhin auftreten, kontaktiere uns unter {contactLink}.</p>
      </>
    )
  },
  [OfferCheckoutStatus.LOAD_FAILED]: {
    icon: CircleAlert,
    title: 'Das Angebot konnte nicht geladen werden',
    body: (
      <>
        <p>Bitte versuche es erneut.</p>
        <p>Sollte dieser Fehler weiterhin auftreten, kontaktiere uns unter {contactLink}.</p>
      </>
    ),
    action: (
      <Button type="button" className={ACTION_CLASS} onClick={() => window.location.reload()}>
        Seite neu laden
      </Button>
    )
  },
  // The link to set the password goes out by mail only – never show it here.
  [OfferCheckoutStatus.ACCEPTED]: {
    icon: MailCheck,
    title: 'Angebot angenommen!\nWillkommen bei 7i7®',
    body: (
      <>
        <p>Wir haben dir soeben zwei E-Mails gesendet:</p>
        <ul className="flex flex-col gap-2 text-left">
          <MailItem icon={KeyRound}>
            <strong>Dein Zugang</strong> - darin legst du dein Passwort fest
          </MailItem>
          <MailItem icon={FileText}>
            <strong>Deine Vertragsbestätigung</strong>
          </MailItem>
        </ul>
        <p>Prüfe bitte auch den Spam-Ordner.</p>
        <p className="text-xs text-[#082729]/50">
          Keine Mail bekommen? Über{' '}
          <a href="/reset" className={LINK_CLASS}>
            „Passwort vergessen?“
          </a>{' '}
          kannst du dir jederzeit einen neuen Link schicken lassen.
        </p>
      </>
    )
  }
};

export interface OfferCheckoutNoticeProps {
  status: OfferCheckoutNoticeStatus;
}

/** Full-page message of the public checkout – invalid or used link, rate limit, load error and the success after accepting. */
const OfferCheckoutNotice = ({ status }: OfferCheckoutNoticeProps) => {
  const { icon: Icon, title, body, action } = NOTICES[status];

  return (
    <main className="flex min-h-screen items-center justify-center bg-white p-4">
      <section className="flex w-full max-w-md flex-col items-center gap-6 rounded-2xl bg-[#E8F6F6] px-6 py-10 text-center lg:px-10 lg:py-14">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white">
          <Icon className="h-6 w-6 text-[#082729]" />
        </span>
        <h1 className="text-2xl font-bold leading-tight text-[#082729]">{title}</h1>
        <div className="flex w-full flex-col gap-4 text-sm leading-relaxed text-[#082729]">{body}</div>
        {action}
      </section>
    </main>
  );
};

export default OfferCheckoutNotice;
